const invocationCounters = new WeakMap();
/**
 * Prefers doing the thing over not doing the thing.
 *
 * Calls your callback immediately the first time the returned wrapper is called, then again
 * after `delay` ms if the wrapper is called again during that time period, and every `delay` ms
 * thereafter until the wrapper has not been called for `delay` ms.
 *
 * Always calls the callback exactly once following the last invocation of the wrapper. If the
 * wrapper is invoked twice within `delay` ms (once to initiate, and once before `delay` ms has
 * passed) your callback will be invoked exactly twice, immediately and after `delay` ms.
 *
 * **Examples:**
 *
 * Delay: 100ms
 * - 2 Wrapper Invocations: 0ms, 1ms
 *   - 2 Callback Invocations: 0ms, 100ms
 * - 2 Wrapper Invocations: 0ms, 33ms
 *   - 2 Callback Invocations: 0ms, 100ms
 * - 3 Wrapper Invocations: 0ms, 87ms, 101ms
 *   - 3 Callback Invocations: 0ms, 100ms, 200ms
 * - 2 Wrapper Invocations: 0ms, 101ms
 *   - 2 Callback Invocations: 0ms, 101ms (because the delay expired between the invocations)
 * - 8 Wrapper Invocations: 0ms, 66ms, 189ms, 199ms, 210ms, 212ms, 236ms, 268ms
 *   - 4 Callback Invocations: 0ms, 100ms, 200ms, 300ms
 * - 10 Wrapper Invocations: 0ms, 0ms, 0ms, 0ms, 0ms, 0ms, 0ms, 0ms, 0ms, 0ms
 *   - 2 Callback Invocations: 0ms, 100ms
 *
 * Delay: 199ms
 * - 2 Wrapper Invocations: 0ms, 1ms
 *   - 2 Callback Invocations: 0ms, 199ms
 * - 2 Wrapper Invocations: 0ms, 198ms
 *   - 2 Callback Invocations: 0ms, 199ms
 * - 2 Wrapper Invocations: 0ms, 199ms
 *   - 2 Callback Invocations: 0ms, 199ms
 * - 2 Wrapper Invocations: 0ms, 200ms
 *   - 2 Callback Invocations: 0ms, 200ms
 * - 8 Wrapper Invocations: 0ms, 66ms, 189ms, 199ms, 210ms, 212ms, 236ms, 268ms
 *   - 3 Callback Invocations: 0ms, 199ms, 398ms
 *
 */
export const guaranteedThrottle = (delay, callback) => () => {
    if (invocationCounters.has(callback)) {
        // If there is already a check scheduled, just update the last invocation timestamp.
        // This will cause the callback to be fired when the scheduled check runs.
        invocationCounters.set(callback, invocationCounters.get(callback) + 1);
        return;
    }
    else {
        invocationCounters.set(callback, 0);
        callCallbackAndScheduleNextCheck(0);
        function callCallbackAndScheduleNextCheck(thisInvocation) {
            callback();
            window.setTimeout(() => {
                // Has the throttle wrapper been called since the first execution?
                const lastInvocation = invocationCounters.get(callback);
                if (lastInvocation && lastInvocation !== thisInvocation) {
                    // If so, call the callback and schedule another check in `delay` ms
                    callCallbackAndScheduleNextCheck(lastInvocation);
                }
                else {
                    // If not, clear the record of the last invocation
                    invocationCounters.delete(callback);
                }
            }, delay);
        }
    }
};
